import React, { useState, useEffect } from 'react';
import * as _ from 'lodash';
import * as XLSX from 'xlsx-js-style';
import { Button, Box, Flex, Checkbox, TextField, Icon, IconButton, Label, Text, Dropdown } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { AppModal, AppModalContent, AppModalFooter } from '@gorilla/ui/src/components/AppModal';
import {
  ExternalPage,
  TurnInto,
  Column as ColumnIcon,
  Item as ItemIcon,
  Board as BoardIcon,
  Formula as FormulaIcon,
  Alert,
  MoveArrowRight,
  ConvertToItem,
  ConvertToSubitem,
} from '@vibe/icons';
import { Template, Sheet, FieldGroup } from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/types';
import { chatatABC } from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/spreadsheet-manager';
import { ErrorBox } from '@gorilla/ui/src/components/ErrorBox';
import { useSpreadsheetLoader } from '../hooks/use-spreadsheet-loader';
import { Board, updateItemValue } from '@gorilla/common/src/lib/monday-api/api';
import { useExecutionQueue } from '../shared/hooks/use-execution-queue';
import { useBoard } from '../shared/hooks/use-boards';
import useMonday from '../hooks/use-monday';
import { QueueItem } from '../shared/utils/execution-queue';
import { createSignal } from '../services/signals';
import { monday, mondayClient } from '../services/monday';
import { EngineBoardItem } from '@gorilla/common/src/lib/engine/engine';

interface DiffResult {
  itemId: number;
  itemName: string;
  prevValue: string | number | undefined;
  targetValue: string | number | undefined;
  targetColumnTitle: string | undefined;
  type: 'changed' | 'unchanged' | 'updating' | 'updated';
}

function prepareDiffData(
  workbook: XLSX.WorkBook,
  sheet: Sheet,
  fieldGroup: FieldGroup,
  formulaColumnIdx: number,
  targetColumnId: string,
  targetBoard: Board,
) {
  const sheetjsSheet = Object.values(workbook.Sheets).find((s) => s['!sheetId'] === sheet.id);

  if (!sheetjsSheet) {
    throw new Error('Worksheet not found');
  }

  const startRowIdx = sheetjsSheet[`!field_group_${fieldGroup.id}_start_row_idx`] as number;
  const endRowIdx = sheetjsSheet[`!field_group_${fieldGroup.id}_end_row_idx`] as number;

  if (!_.isNumber(startRowIdx) || !_.isNumber(endRowIdx)) {
    return [];
  }

  const targetColumn = targetBoard.columns.find((column) => column.id === targetColumnId);

  const results: DiffResult[] = [];

  for (let rowIdx = startRowIdx; rowIdx <= endRowIdx; rowIdx++) {
    const formulaCell = sheetjsSheet[`${chatatABC(formulaColumnIdx)}${rowIdx + 1}`];
    const boardItem = sheetjsSheet[`!row_${rowIdx}_item`] as EngineBoardItem;

    const prevBoardItemValue = boardItem.values.find((value) => {
      return value.column_id === targetColumnId;
    });

    const prevValue = _.get(prevBoardItemValue, 'value.value');
    const targetValue = formulaCell?.v;

    results.push({
      itemId: boardItem.id,
      itemName: boardItem.name,
      prevValue: prevValue,
      targetValue: targetValue,
      targetColumnTitle: targetColumn?.title,
      type: prevValue === targetValue ? 'unchanged' : 'changed',
    });
  }

  return results;
}

interface ToMondayModalContext {
  targetColumnId?: string;
  onlyChangedValues?: boolean;
  isQuickAction?: boolean;
  quickActionName?: string;
}

type Path = string[];

type DiffTableRowProps = {
  diffResult: DiffResult;
  onUpdateClick: () => void;
  updateStatus: 'pending' | 'resolved' | 'rejected' | null;
  updateError: string | null;
};

function DiffTableRow({ diffResult, onUpdateClick, updateStatus, updateError }: DiffTableRowProps) {
  let status: any;

  if (updateStatus === 'pending') {
    status = <Label text="Updating item" size="small" color="dark" />;
  } else if (updateStatus === 'resolved') {
    status = <Label text="Value updated" size="small" color="positive" />;
  } else if (updateStatus === 'rejected') {
    status = <Label text={`Error: ${updateError}`} size="small" color="negative" />;
  } else if (diffResult.type === 'changed') {
    status = <Label text="Update available" size="small" />;
  } else if (diffResult.type === 'unchanged') {
    status = <Label text="Values match" size="small" color="dark" kind="line" />;
  }

  let diff: any = null;

  if (diffResult.prevValue === diffResult.targetValue) {
    diff = (
      <>
        <td className="before" style={{ color: 'rgba(50, 51, 56, var(--disabled-component-opacity))' }}>
          {diffResult.prevValue}
        </td>
        <td className="operator" style={{ color: 'rgba(50, 51, 56, var(--disabled-component-opacity))' }}>
          <Flex justify="center">
            <Icon icon={MoveArrowRight} />
          </Flex>
        </td>
        <td className="after" style={{ color: 'rgba(50, 51, 56, var(--disabled-component-opacity))' }}>
          <strong>{diffResult.targetValue}</strong>
        </td>
      </>
    );
  } else if (updateStatus === 'resolved') {
    diff = (
      <>
        <td className="before" style={{ textDecoration: 'line-through', color: '#d83a52' }}>
          {diffResult.prevValue}
        </td>
        <td className="operator">
          <Flex justify="center">
            <Icon icon={MoveArrowRight} />
          </Flex>
        </td>
        <td className="after" style={{ color: '#258750' }}>
          <strong>{diffResult.targetValue}</strong>
        </td>
      </>
    );
  } else {
    diff = (
      <>
        <td className="before">
          {diffResult.prevValue === null || diffResult.prevValue === null ? <span>&nbsp;</span> : <span>{diffResult.prevValue}</span>}
        </td>
        <td className="operator">
          <Flex justify="center">
            <Icon icon={MoveArrowRight} />
          </Flex>
        </td>
        <td className="after">
          {diffResult.targetValue === null || diffResult.targetValue === undefined ? (
            <span>&nbsp;</span>
          ) : (
            <strong>{diffResult.targetValue}</strong>
          )}
        </td>
      </>
    );
  }

  return (
    <tr className={`${diffResult.type === 'unchanged' ? 'unchanged' : ''}`}>
      <td>
        <Flex gap={12}>
          <IconButton
            icon={ExternalPage}
            size="xs"
            kind="tertiary"
            onClick={() => {
              monday.execute('openItemCard', {
                itemId: diffResult.itemId,
              });
            }}
          />
          <span>{diffResult.itemName}</span>
          {status}
        </Flex>
      </td>
      <td className="colname">
        <span>{diffResult.targetColumnTitle}</span>
      </td>
      {diff}
      <td className="action">
        <Flex gap={12} justify="center">
          <Button
            disabled={diffResult.type !== 'changed' || !!updateStatus}
            size="xs"
            kind="secondary"
            onClick={async () => {
              createSignal('to_monday_update');
              onUpdateClick();
            }}
          >
            Update item
          </Button>
        </Flex>
      </td>
    </tr>
  );
}

type DiffTableProps = {
  diffData: ReturnType<typeof prepareDiffData>;
  queue: Map<string | number, QueueItem>;
  onUpdateClick: (diffResult: DiffResult) => void;
  onlyChangedValues: boolean;
  onChangeOnlyChangedValues: (onlyChangedValues: boolean) => void;
  targetColumnId: string;
  targetBoard: Board;
};

function DiffTable({
  diffData,
  onChangeOnlyChangedValues,
  onlyChangedValues,
  targetBoard,
  targetColumnId,
  queue,
  onUpdateClick,
}: DiffTableProps) {
  if (diffData.length === 0) {
    return <div>Oops, no items found.</div>;
  }

  const filteredDiffData = onlyChangedValues ? diffData.filter((diffResult) => diffResult.type !== 'unchanged') : diffData;

  return (
    <div style={{ fontSize: '14px' }}>
      <table className="ToMondayTable">
        <thead>
          <tr>
            <th>
              <Flex gap={8} align="center">
                <Icon icon={ItemIcon} size={20} />
                <span>Item name</span>
              </Flex>
            </th>
            <th className="colname">
              <Flex gap={8} align="center" justify="center">
                <Icon icon={ColumnIcon} size={20} />
                <span>Column</span>
              </Flex>
            </th>
            <th className="before">
              <Flex gap={8} align="center" justify="center">
                <Icon icon={ConvertToItem} size={20} />
                <span>Value before</span>
              </Flex>
            </th>
            <th className="operator">&nbsp;</th>
            <th className="after">
              <Flex gap={8} align="center" justify="center">
                <Icon icon={ConvertToSubitem} size={20} />
                <span>Value after</span>
              </Flex>
            </th>
            <th className="action">&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {filteredDiffData.length === 0 ? (
            <tr>
              <td colSpan={6} rowSpan={2} style={{ textAlign: 'center' }}>
                No items match the filter criteria.
              </td>
            </tr>
          ) : null}

          {filteredDiffData.map((diffResult, i) => {
            const queueItem = queue.get(diffResult.itemId);
            let updateError: string | null = null;

            if (_.get(queueItem, 'error.data.errorCode') === 'ColumnValueException') {
              updateError = 'mismatch between column type and value type';
            } else if (_.get(queueItem, 'error.data.errorMessage')) {
              updateError = _.get(queueItem, 'error.data.errorMessage')!;
            } else if (_.get(queueItem, 'error.message')) {
              updateError = _.get(queueItem, 'error.message')!;
            } else if (_.get(queueItem, 'error')) {
              updateError = 'unknown error';
            }

            return (
              <DiffTableRow
                key={diffResult.itemId}
                updateStatus={queueItem ? queueItem.status : null}
                updateError={updateError}
                diffResult={diffResult}
                onUpdateClick={() => {
                  onUpdateClick(diffResult);
                }}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type SummaryStepProps = {
  onClose: () => void;
  ctx: ToMondayModalContext;
  onCtxChange: (ctx: ToMondayModalContext) => void;
  onPathChange?: (path: Path) => void;
  formulaColumnIdx: number;
  template: Template;
  targetBoard: Board;
  fieldGroup: FieldGroup;
  showBackButton: boolean;
  sheet: Sheet;
  updateAllOnLoad?: boolean;
  setModalFullWidth: any;
};

function SummaryStep({
  ctx,
  onClose,
  onCtxChange,
  template,
  onPathChange,
  fieldGroup,
  sheet,
  formulaColumnIdx,
  targetBoard,
  setModalFullWidth,
  updateAllOnLoad = false,
  showBackButton = false,
}: SummaryStepProps) {
  const { loadingMessage, data, error } = useSpreadsheetLoader(_.get(template, 'id'), 'sheetjs', true, false);
  const { add, queueSize, isRunning, queue, getQueueDonePromise } = useExecutionQueue();
  let diffData: ReturnType<typeof prepareDiffData> | null = null;

  if (data) {
    diffData = prepareDiffData(data as XLSX.WorkBook, sheet, fieldGroup, formulaColumnIdx, ctx.targetColumnId as string, targetBoard);
  }

  let changedDiffData: ReturnType<typeof prepareDiffData> | null = null;

  if (diffData) {
    changedDiffData = diffData.filter((diffResult) => diffResult.type === 'changed');
  }

  function updateAll() {
    if (changedDiffData) {
      changedDiffData.forEach((diffResult) => {
        add(diffResult.itemId, async () => {
          await updateItemValue(mondayClient, targetBoard.id, diffResult.itemId, ctx.targetColumnId!, `${diffResult.targetValue}`);
        });
      });
    }
  }

  const allUpdating = !!(changedDiffData && changedDiffData.length === queue.size);

  useEffect(() => {
    setModalFullWidth(true);

    if (data) {
      (async () => {
        if (updateAllOnLoad) {
          updateAll();
          await getQueueDonePromise();
          onClose();
        }
      })();
    }
  }, [data]);

  return (
    <div style={{ marginTop: '20px' }}>
      <AppModalContent>
        <div
          style={{
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 240px)',
            border: '1px solid #d0d4e4',
            borderRadius: '8px',
          }}
        >
          <div>
            {diffData ? (
              <DiffTable
                queue={queue}
                diffData={diffData}
                onlyChangedValues={!!ctx.onlyChangedValues}
                onChangeOnlyChangedValues={(onlyChangedValues) => {
                  onCtxChange({ ...ctx, onlyChangedValues });
                }}
                targetColumnId={ctx.targetColumnId as string}
                targetBoard={targetBoard}
                onUpdateClick={(diffResult) => {
                  add(diffResult.itemId, async () => {
                    await updateItemValue(
                      mondayClient,
                      targetBoard.id,
                      diffResult.itemId,
                      ctx.targetColumnId!,
                      `${diffResult.targetValue}`,
                    );
                  });
                }}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  minHeight: '178px',
                }}
              >
                {error ? <ErrorBox error={error} mondayClient={mondayClient} /> : <Text type="text2">{loadingMessage}</Text>}
              </div>
            )}
          </div>
        </div>
      </AppModalContent>
      <AppModalFooter>
        <Flex>
          <Flex gap={12}>
            <Checkbox
              label="Only show items with available updates"
              disabled={!changedDiffData || changedDiffData.length === 0}
              checked={!!ctx.onlyChangedValues}
              onChange={(ev) => {
                onCtxChange({ ...ctx, onlyChangedValues: ev.target.checked });
              }}
            />
          </Flex>
          <Box style={{ marginLeft: 'auto' }}>
            <Flex gap={12} justify="end">
              {showBackButton ? (
                <Button
                  kind="tertiary"
                  onClick={() => {
                    if (onPathChange) {
                      onPathChange([]);
                    }
                    setModalFullWidth(false);
                  }}
                >
                  Back
                </Button>
              ) : null}
              <Button
                disabled={allUpdating}
                loading={isRunning}
                leftIcon={TurnInto}
                onClick={async () => {
                  createSignal('to_monday_update_all');
                  updateAll();
                }}
              >
                Bulk update
              </Button>
            </Flex>
          </Box>
        </Flex>
      </AppModalFooter>
    </div>
  );
}

type BoardColumnSelectionStepProps = {
  onClose: () => void;
  ctx: ToMondayModalContext;
  onCtxChange: (ctx: ToMondayModalContext) => void;
  onPathChange: (path: Path) => void;
  onSaveSettings: () => void;

  formulaColumnIdx: number;
  template: Template;
  targetBoard: Board;

  path: Path;
  formula: string;

  setModalFullWidth: any;
};

function BoardColumnSelectionStep({
  ctx,
  path,
  onSaveSettings,
  onClose,
  onCtxChange,
  onPathChange,
  formulaColumnIdx,
  template,
  targetBoard,
  formula,
}: BoardColumnSelectionStepProps) {
  const columnOptions = targetBoard.columns
    .filter((column) => column.type === 'text' || column.type === 'long_text' || column.type === 'numbers')
    .map((column) => ({
      label: column.title,
      value: column.id,
      startElement: { type: 'icon' as const, value: ColumnIcon },
    }));

  const selectedColumnOption = columnOptions.find((option) => option.value === ctx.targetColumnId);

  return (
    <>
      <AppModalContent>
        <Box marginTop="medium" padding="large" backgroundColor="allgreyBackgroundColor" rounded="small" border={true}>
          <>
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                marginBottom: '10px',
                padding: '9px 17px',
                borderRadius: '4px',
                border: '1px solid #c3c6d4',
                backgroundColor: '#fff',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <Icon icon={FormulaIcon} size={18} />
                <Text type="text2">Formula</Text>
              </div>
              <div
                style={{
                  marginLeft: 'auto',
                  textAlign: 'right',
                  maxWidth: '320px',
                  width: 'auto',
                }}
              >
                <Text type="text2" ellipsis style={{ fontWeight: 600, textAlign: 'right' }}>
                  {formula}
                </Text>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                marginBottom: '10px',
                padding: '9px 17px',
                borderRadius: '4px',
                border: '1px solid #c3c6d4',
                backgroundColor: '#fff',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <Icon icon={BoardIcon} size={18} />
                <Text type="text2">Target board</Text>
              </div>
              <div
                style={{
                  marginLeft: 'auto',
                  textAlign: 'right',
                  maxWidth: '320px',
                  width: 'auto',
                }}
              >
                <Text type="text2" ellipsis style={{ fontWeight: 600, textAlign: 'right' }}>
                  {targetBoard.name}
                </Text>
              </div>
            </div>
            <Dropdown
              searchable
              onOptionSelect={(ev: any) => {
                onCtxChange({ ...ctx, targetColumnId: ev.value });
              }}
              options={columnOptions}
              placeholder={`Select board column to update`}
              value={dropdownValue(selectedColumnOption)}
              clearable={false}
              size="medium"
            />
            <div style={{ margin: '20px 0 15px' }}>
              <Checkbox
                label="Quick Action: Update board items from your dashboard with a button"
                checked={!!ctx.isQuickAction}
                onChange={(ev) => {
                  onCtxChange({ ...ctx, isQuickAction: ev.target.checked });
                }}
              />
            </div>
            <Flex gap={16} align="end">
              <div style={{ flex: 1 }}>
                <TextField
                  placeholder="e.g. Update budget"
                  //title="Button label"
                  size="medium"
                  value={ctx.quickActionName}
                  maxLength={20}
                  onChange={(newVal) => {
                    onCtxChange({ ...ctx, quickActionName: newVal });
                  }}
                  disabled={!ctx.isQuickAction}
                />
              </div>

              <div>
                <Button
                  kind="secondary"
                  onClick={() => {
                    onSaveSettings();
                  }}
                >
                  Save settings
                </Button>
              </div>
            </Flex>
          </>
        </Box>
      </AppModalContent>
      <AppModalFooter>
        <Flex gap={5} justify="end">
          <Box marginEnd="auto">
            <div style={{ fontSize: '14px', color: '#afb0bd' }}>
              Only <em>Text</em> and <em>Numeric</em> columns are currently supported.
            </div>
          </Box>
          <Flex>
            <Box style={{ marginLeft: 'auto' }}>
              <Flex gap={12} justify="end">
                <Button
                  kind="tertiary"
                  onClick={() => {
                    onClose();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!ctx.targetColumnId}
                  onClick={() => {
                    onPathChange(['configuration']);

                    const localStorageKey = `to-monday-modal-column::${template.id}::${formulaColumnIdx}`;
                    localStorage.setItem(localStorageKey, ctx.targetColumnId!);
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </AppModalFooter>
    </>
  );
}

type ToMondaySettings = {
  to_monday_target_column_id?: string;
  to_monday_is_quick_actions?: boolean;
  to_monday_quick_action_name?: string;
};

type ToMondayModalProps = {
  onClose: () => void;
  onSaveSettings: (settings: ToMondaySettings) => void;
  show: boolean;
  initialCtx?: ToMondayModalContext;

  formulaColumnIdx: number;
  template: Template;
  targetBoard: Board;
  fieldGroup: FieldGroup;
  sheet: Sheet;
  formula: string;
};

export function ToMondayModal({
  show,
  onClose,
  initialCtx,
  formulaColumnIdx,
  formula,
  template,
  targetBoard,
  fieldGroup,
  onSaveSettings,
  sheet,
}: ToMondayModalProps) {
  const [path, setPath] = useState<Path>([]);
  const [ctx, setCtx] = useState<ToMondayModalContext>(initialCtx || {});
  const [modalFullWidth, setModalFullWidth] = useState(false);
  const { mondayContext } = useMonday();

  let stepDescription = '';
  let modalContent = <></>;

  if (import.meta.env.MODE !== 'development' && _.get(mondayContext, 'appVersion.versionData.major')! < 4) {
    stepDescription =
      'The app needs permission to update boards for this functionality to work. Please ask an admin of your monday account to update the app.';
  } else if (path.length === 0) {
    stepDescription =
      'The formula below will be executed in the target board for each item in the column you select. In the next step you can review all values before applying any changes.';

    modalContent = (
      <BoardColumnSelectionStep
        onClose={onClose}
        ctx={ctx}
        onCtxChange={(ctx) => setCtx(ctx)}
        onPathChange={(path) => setPath(path)}
        template={template}
        targetBoard={targetBoard}
        onSaveSettings={() => {
          onSaveSettings({
            to_monday_target_column_id: ctx.targetColumnId,
            to_monday_is_quick_actions: ctx.isQuickAction,
            to_monday_quick_action_name: ctx.quickActionName,
          });
        }}
        formula={formula}
        formulaColumnIdx={formulaColumnIdx}
        path={path}
        setModalFullWidth={setModalFullWidth}
      />
    );
  } else if (path.length === 1) {
    modalContent = (
      <SummaryStep
        onClose={onClose}
        ctx={ctx}
        onCtxChange={(ctx) => setCtx(ctx)}
        onPathChange={(path) => setPath(path)}
        showBackButton={true}
        template={template}
        targetBoard={targetBoard}
        formulaColumnIdx={formulaColumnIdx}
        fieldGroup={fieldGroup}
        sheet={sheet}
        setModalFullWidth={setModalFullWidth}
      />
    );
  }

  return (
    <AppModal
      show={show}
      onClose={() => onClose()}
      width={modalFullWidth ? 'full-width' : 'default'}
      alertDialog={true}
      data-testid="to-monday-modal"
    >
      <Flex gap={8} direction="column" justify="start" align="start">
        <Flex gap={8}>
          <Icon icon={TurnInto} size={20} />
          <div style={{ maxWidth: '200px' }}>
            <Text type="text1" weight="medium" ellipsis>
              Update items
            </Text>
          </div>
        </Flex>
        {stepDescription !== '' ? (
          <Text type="text2" weight="normal" ellipsis={false}>
            {stepDescription}
          </Text>
        ) : null}
      </Flex>
      {modalContent}
    </AppModal>
  );
}

{
  /* Dashboard Button Modal */
}

type InstantUpdateModalProps = {
  onClose: () => void;
  show: boolean;
  initialCtx?: ToMondayModalContext;

  formulaColumnIdx: number;
  template: Template;
  fieldGroup: FieldGroup;
  sheet: Sheet;
};

export function InstantUpdateModal({ show, onClose, initialCtx, formulaColumnIdx, template, fieldGroup, sheet }: InstantUpdateModalProps) {
  const [ctx, setCtx] = useState<ToMondayModalContext>(initialCtx || {});
  const [modalFullWidth, setModalFullWidth] = useState(false);
  const { board, isLoading, error } = useBoard(fieldGroup.board_id);
  let content: any = null;

  if (isLoading) {
    content = (
      <AppModalContent>
        <Text type="text2" weight="normal" ellipsis={false}>
          Loading...
        </Text>
      </AppModalContent>
    );
  } else if (fieldGroup.from_context) {
    content = (
      <AppModalContent>
        <Text type="text2" weight="normal" ellipsis={false}>
          Error: Dynamic boards not supported
        </Text>
      </AppModalContent>
    );
  } else if (error) {
    content = (
      <AppModalContent>
        <Text type="text2" weight="normal" ellipsis={false}>
          Error: {error?.message}
        </Text>
      </AppModalContent>
    );
  } else if (!board) {
    content = (
      <AppModalContent>
        <Text type="text2" weight="normal" ellipsis={false}>
          Error: Could not load board
        </Text>
      </AppModalContent>
    );
  } else {
    content = (
      <SummaryStep
        onClose={onClose}
        ctx={ctx}
        onCtxChange={(ctx) => setCtx(ctx)}
        // not needed
        template={template}
        targetBoard={board}
        formulaColumnIdx={formulaColumnIdx}
        fieldGroup={fieldGroup}
        sheet={sheet}
        showBackButton={false}
        updateAllOnLoad={false}
        setModalFullWidth={setModalFullWidth}
      />
    );
  }

  return (
    <AppModal
      show={show}
      onClose={() => onClose()}
      width={modalFullWidth ? 'full-width' : 'default'}
      alertDialog={true}
      data-testid="instant-update-modal"
    >
      <Flex gap={8}>
        <Icon icon={TurnInto} size={20} />
        <div style={{ maxWidth: '200px' }}>
          <Text type="text1" weight="medium" ellipsis>
            Update items
          </Text>
        </div>
      </Flex>
      <div style={{ marginTop: '20px' }}>{content}</div>
    </AppModal>
  );
}
